import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BreederOnboarding = ({ user, checkAuth }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const activationRecoveryStarted = useRef(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    business_name: "",
    description: "",
    location_city: "",
    location_state: "",
    location_zip: "",
    species: [],
    exotic_subtypes: [],
    services: [],
    breeds: [],
    owner_attestation: false,
    exotics_compliance_attested: false
  });

  useEffect(() => {
    if (activationRecoveryStarted.current) return;

    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (params.get("activation_success") !== "true" || !sessionId) return;

    activationRecoveryStarted.current = true;
    const reconcileActivation = async () => {
      try {
        const { data } = await axios.post(
          `${API}/subscription/reconcile-activation`,
          null,
          { params: { session_id: sessionId }, withCredentials: true }
        );
        await checkAuth();
        const memberNumber = data.founding_member_number
          ? ` Founding Member #${data.founding_member_number} is confirmed.`
          : "";
        toast.success(`Payment verified and breeder tier activated.${memberNumber}`);
        navigate(location.pathname, { replace: true });
      } catch (error) {
        toast.error(
          error.response?.data?.detail ||
          "Your payment succeeded, but activation still needs attention. Please refresh to retry."
        );
        activationRecoveryStarted.current = false;
      }
    };

    reconcileActivation();
  }, [checkAuth, location.pathname, location.search, navigate]);

  const speciesOptions = [
    { value: "equine", label: "Equine (Horses)" },
    { value: "canine", label: "Canine (Dogs)" },
    { value: "feline", label: "Feline (Cats)" },
    { value: "cattle", label: "Cattle" },
    { value: "exotics", label: "Exotics (Permitted Only)" }
  ];

  const exoticSubtypes = [
    { value: "birds", label: "Birds" },
    { value: "miniature_primates", label: "Miniature Primates" }
  ];

  const serviceOptions = [
    { value: "sell_animals", label: "Sell Animals" },
    { value: "stud_services", label: "Stud Services" },
    { value: "mare_services", label: "Mare Services" },
    { value: "breeder_transport", label: "Breeder-Provided Transport" }
  ];

  const handleSpeciesToggle = (species) => {
    setFormData(prev => ({
      ...prev,
      species: prev.species.includes(species)
        ? prev.species.filter(s => s !== species)
        : [...prev.species, species]
    }));
  };

  const handleExoticToggle = (subtype) => {
    setFormData(prev => ({
      ...prev,
      exotic_subtypes: prev.exotic_subtypes.includes(subtype)
        ? prev.exotic_subtypes.filter(s => s !== subtype)
        : [...prev.exotic_subtypes, subtype]
    }));
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/breeder/onboard`, formData, { withCredentials: true });
      await checkAuth();
      toast.success("Breeder profile created!");
      navigate('/dashboard/breeder');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create profile");
    } finally {
      setLoading(false);
    }
  };

  const showExoticsSection = formData.species.includes('exotics');

  return (
    <div className="pt-24 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-2">Breeder Onboarding</p>
          <h1 className="text-4xl text-[#EDEDED] mb-2">Join Breedling</h1>
          <p className="text-[#A1A1AA]">Create your breeder profile and start listing.</p>
        </div>

        <div className="glass-card p-8">
          {step === 1 && (
            <div className="space-y-6">
              <h3 className="text-xl text-[#EDEDED] mb-4">Business Information</h3>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Business Name *</label>
                <input
                  type="text"
                  value={formData.business_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                  className="input-minimal"
                  placeholder="Your Ranch or Kennel Name"
                  data-testid="business-name-input"
                />
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="input-minimal min-h-[100px]"
                  placeholder="Tell buyers about your breeding program..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">City *</label>
                  <input
                    type="text"
                    value={formData.location_city}
                    onChange={(e) => setFormData(prev => ({ ...prev, location_city: e.target.value }))}
                    className="input-minimal"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">State *</label>
                  <input
                    type="text"
                    value={formData.location_state}
                    onChange={(e) => setFormData(prev => ({ ...prev, location_state: e.target.value }))}
                    className="input-minimal"
                    placeholder="State"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">ZIP Code *</label>
                <input
                  type="text"
                  value={formData.location_zip}
                  onChange={(e) => setFormData(prev => ({ ...prev, location_zip: e.target.value }))}
                  className="input-minimal"
                  placeholder="ZIP"
                />
              </div>

              <button
                onClick={() => setStep(2)}
                disabled={!formData.business_name || !formData.location_city || !formData.location_state || !formData.location_zip}
                className="btn-gold w-full disabled:opacity-50"
              >
                Continue
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h3 className="text-xl text-[#EDEDED] mb-4">Species & Services</h3>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-3">What species do you breed? *</label>
                <div className="grid grid-cols-2 gap-3">
                  {speciesOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handleSpeciesToggle(opt.value)}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        formData.species.includes(opt.value)
                          ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                          : "border-white/10 text-[#A1A1AA] hover:border-white/20"
                      }`}
                      data-testid={`species-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {showExoticsSection && (
                <div className="p-4 border border-yellow-500/30 rounded-lg bg-yellow-500/5">
                  <p className="text-yellow-400 text-sm mb-3">⚠️ Exotics Compliance Required</p>
                  <p className="text-[#A1A1AA] text-sm mb-4">
                    Only permitted species allowed: Birds and Miniature Primates.
                    Reptiles, big cats, and protected species are strictly prohibited.
                  </p>

                  <label className="block text-sm text-[#A1A1AA] mb-3">Select exotic subtypes:</label>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {exoticSubtypes.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => handleExoticToggle(opt.value)}
                        className={`p-3 rounded-lg border text-left transition-colors ${
                          formData.exotic_subtypes.includes(opt.value)
                            ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                            : "border-white/10 text-[#A1A1AA] hover:border-white/20"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.exotics_compliance_attested}
                      onChange={(e) => setFormData(prev => ({ ...prev, exotics_compliance_attested: e.target.checked }))}
                      className="mt-1"
                    />
                    <span className="text-sm text-[#A1A1AA]">
                      I certify all animals are lawfully owned and permitted for sale under applicable federal, state, and local laws.
                    </span>
                  </label>
                </div>
              )}

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-3">What services do you offer? *</label>
                <div className="grid grid-cols-2 gap-3">
                  {serviceOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handleServiceToggle(opt.value)}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        formData.services.includes(opt.value)
                          ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                          : "border-white/10 text-[#A1A1AA] hover:border-white/20"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-4">
                <button onClick={() => setStep(1)} className="btn-secondary flex-1">Back</button>
                <button
                  onClick={() => setStep(3)}
                  disabled={formData.species.length === 0 || formData.services.length === 0 || (showExoticsSection && !formData.exotics_compliance_attested)}
                  className="btn-gold flex-1 disabled:opacity-50"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <h3 className="text-xl text-[#EDEDED] mb-4">Attestation</h3>

              <div className="p-4 border border-white/10 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.owner_attestation}
                    onChange={(e) => setFormData(prev => ({ ...prev, owner_attestation: e.target.checked }))}
                    className="mt-1"
                    data-testid="owner-attestation"
                  />
                  <span className="text-[#A1A1AA]">
                    I am the owner or authorized agent permitted to transact on behalf of the animals I will list on Breedling. I agree to comply with all applicable laws and Breedling&apos;s terms of service.
                  </span>
                </label>
              </div>

              <div className="p-4 bg-[#0A0A0A] rounded-lg">
                <h4 className="text-[#EDEDED] mb-2">Summary</h4>
                <p className="text-sm text-[#A1A1AA]"><strong>Business:</strong> {formData.business_name}</p>
                <p className="text-sm text-[#A1A1AA]"><strong>Location:</strong> {formData.location_city}, {formData.location_state}</p>
                <p className="text-sm text-[#A1A1AA]"><strong>Species:</strong> {formData.species.join(', ')}</p>
                <p className="text-sm text-[#A1A1AA]"><strong>Services:</strong> {formData.services.join(', ')}</p>
              </div>

              <div className="flex gap-4">
                <button onClick={() => setStep(2)} className="btn-secondary flex-1">Back</button>
                <button
                  onClick={handleSubmit}
                  disabled={!formData.owner_attestation || loading}
                  className="btn-gold flex-1 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-onboarding"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Create Profile
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BreederOnboarding;
